import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { MyClubPageHeader } from '@sorare/core/src/components/navigation/MyClubPageHeader';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG_LEADERBOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { NoIndex, useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { NewHeader } from '../NewHeader';
import {
  NewCollectionLayoutQuery,
  NewCollectionLayoutQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(FullWidthContainer)`
  background-color: var(--c-black);
  color: var(--c-white);
  min-height: var(--100vh);
`;

const NEW_COLLECTION_LAYOUT_QUERY = gql`
  query NewCollectionLayoutQuery($collection: String!, $slug: String!) {
    user(slug: $slug) {
      slug
      nickname
      ...MyClubPageHeader_user
    }
    cardCollection(slug: $collection) {
      slug
      name
      slotsCount
      slots {
        id
      }
      userCardCollection(forUserSlug: $slug) {
        id
        slots {
          slug
        }
        ...NewHeader_userCardCollection
      }
      socialPictureUrls(forUserSlug: $slug) {
        post
        square
        story
      }
      ...NewHeader_cardCollection
    }
  }
  ${NewHeader.fragments.cardCollection}
  ${NewHeader.fragments.userCardCollection}
  ${MyClubPageHeader.fragments.user}
` as TypedDocumentNode<
  NewCollectionLayoutQuery,
  NewCollectionLayoutQueryVariables
>;

type Props = {
  userSlug: string;
  collection: string;
  fromMyClub?: boolean;
};
export const NewCollectionLayout = ({
  userSlug,
  collection,
  fromMyClub,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { isMe } = useCurrentUserContext();

  const { data, loading } = useQuery(NEW_COLLECTION_LAYOUT_QUERY, {
    variables: {
      collection,
      slug: userSlug,
    },
  });
  const { user, cardCollection } = data || {};
  const userCardCollection = cardCollection?.userCardCollection;
  const slots = userCardCollection?.slots || cardCollection?.slots || [];

  useEffect(() => {
    if (cardCollection) {
      const img =
        cardCollection.socialPictureUrls?.post ||
        cardCollection.socialPictureUrls?.square ||
        cardCollection.socialPictureUrls?.story ||
        undefined;

      setPageMetadata(
        formatMessage(
          {
            id: 'Collection.title',
            defaultMessage: "{username}'s collection: {collectionName}",
          },
          { username: user?.nickname, collectionName: cardCollection.name }
        ),
        { img }
      );
    }
  }, [formatMessage, setPageMetadata, cardCollection, user?.nickname]);

  if (loading && !slots.length) {
    return (
      <Wrapper>
        <LoadingIndicator fullHeight />
      </Wrapper>
    );
  }

  if (!cardCollection) {
    return null;
  }

  const cardsPath = isMe(userSlug)
    ? generateSportContextPath(
        ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG,
        {
          params: {
            collectionSlug: collection,
            slug: userSlug,
            collection,
          },
        }
      );

  const leaderboardPath = isMe(userSlug)
    ? generateSportContextPath(
        ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG_LEADERBOARD,
        {
          params: {
            collectionSlug: collection,
            slug: userSlug,
            collection,
          },
        }
      );

  return (
    <>
      <NoIndex />
      {fromMyClub ? (
        user && (
          <MyClubPageHeader
            user={user}
            subtitle={
              <>
                <FormattedMessage
                  id="Collection.subtitle"
                  defaultMessage="Collection"
                />{' '}
                · {cardCollection.team?.name}
              </>
            }
          />
        )
      ) : (
        <PageHeader text={cardCollection.team?.name} />
      )}
      <NewHeader
        cardCollection={cardCollection}
        userCardCollection={userCardCollection}
      />
      <TabBar fullWidth variant="flat">
        <Tab to={cardsPath} matches={[cardsPath]} replace>
          <FormattedMessage {...glossary.cards} />
        </Tab>
        <Tab to={leaderboardPath} matches={[leaderboardPath]} replace>
          <FormattedMessage {...fantasy.leaderboard} />
        </Tab>
      </TabBar>
      <Wrapper>
        <Outlet />
      </Wrapper>
    </>
  );
};
